import { Box, Skeleton, Stack } from "@mui/material";

type SkeletonRowsProps = {
  count?: number;
  height?: number;
};

export function SkeletonRows({ count = 4, height = 72 }: SkeletonRowsProps) {
  return (
    <Stack spacing={1.25}>
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} variant="rounded" height={height} />
      ))}
    </Stack>
  );
}

type CommitmentsSectionSkeletonProps = {
  title?: string;
};

export function CommitmentsSectionSkeleton({
  title,
}: CommitmentsSectionSkeletonProps) {
  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      {title ? (
        <Skeleton variant="text" width="38%" height={32} sx={{ maxWidth: 280 }} />
      ) : null}
      <Stack direction="row" spacing={1}>
        <Skeleton variant="rounded" width={120} height={36} />
        <Skeleton variant="rounded" width={120} height={36} />
        <Skeleton variant="rounded" width={120} height={36} />
      </Stack>
      <Skeleton variant="rounded" height={44} />
      <SkeletonRows count={5} height={64} />
    </Stack>
  );
}

export function NotificationsPageSkeleton() {
  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Skeleton variant="text" width="28%" height={40} />
        <Skeleton variant="rounded" width={140} height={36} />
      </Stack>
      <Skeleton variant="rounded" height={44} />
      <Skeleton variant="rounded" height={48} />
      <SkeletonRows count={6} height={72} />
    </Stack>
  );
}

export function BulkDashboardSkeleton() {
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2}>
        <Skeleton variant="rounded" height={88} sx={{ flex: 1 }} />
        <Skeleton variant="rounded" height={88} sx={{ flex: 1 }} />
        <Skeleton variant="rounded" height={88} sx={{ flex: 1 }} />
      </Stack>
      <Skeleton variant="rounded" height={120} />
      <Skeleton variant="rounded" height={120} />
    </Stack>
  );
}

export function NudgeQuestionsSkeleton() {
  return (
    <Box>
      <Skeleton variant="text" width="42%" height={28} sx={{ mb: 1 }} />
      <Stack spacing={1}>
        <Skeleton variant="rounded" height={18} />
        <Skeleton variant="rounded" height={18} />
        <Skeleton variant="rounded" height={18} width="88%" />
      </Stack>
    </Box>
  );
}

type PageLoadingSpinnerProps = {
  minHeight?: number | string;
  size?: number;
};

export function PageLoadingSpinner({
  minHeight = 320,
  size = 36,
}: PageLoadingSpinnerProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight,
        width: "100%",
      }}
      role="status"
      aria-label="Loading"
    >
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: "50%",
          border: "3px solid",
          borderColor: "lightBlue.main",
          borderTopColor: "reliablyBlue.main",
          animation: "rm-spin 0.85s linear infinite",
          "@keyframes rm-spin": {
            to: { transform: "rotate(360deg)" },
          },
        }}
      />
    </Box>
  );
}
